import {BlockProps, VictoryLabelProps} from 'victory';
import {CSSProperties} from 'react';
import {Dimensions} from 'src/utils/helpers';
import {chartPadding} from 'src/components/victory/chartTheme';
import {toSIFormat, truncatedDecimals} from 'src/utils/formatters';
import {VictoryBarData} from 'src/components/Cards/types';
import {isEmpty} from 'lodash';

export function mergeStyleProps(
  style: VictoryLabelProps['style'],
  overrides: CSSProperties
): VictoryLabelProps['style'] {
  return {
    ...(typeof style === 'object' ? (style as CSSProperties) : {}),
    ...overrides,
  };
}

interface SettingsOptions {
  fullPage?: boolean;
  footerDimensions: Dimensions;
  cardContentDimensions: Dimensions;
  labelWidthPercentage?: number;
}

interface ChartSettings {
  chartWidth: number;
  chartHeight: number;
  chartPadding: BlockProps;
}

type HorizontalChartSettings = ChartSettings & {
  labelsWidth: number;
};

export interface ServiceData {
  rows: VictoryBarData;
}

export function getVictoryChartSettings(opts: SettingsOptions): ChartSettings {
  const defaults = {
    chartHeight: 265,
    chartPadding,
  };

  return {
    chartWidth: opts.cardContentDimensions.width || 450,
    chartHeight: Math.max(
      defaults.chartHeight,
      opts.cardContentDimensions.height - opts.footerDimensions.height
    ),
    chartPadding: {...defaults.chartPadding},
  };
}

export function getHorizontalVictoryChartSettings(opts: SettingsOptions): HorizontalChartSettings {
  const settings = getVictoryChartSettings(opts) as HorizontalChartSettings;

  // Labels occupy (labelWidthPercentage | 30%) of card width
  // If fullPage only use 75% of previous value for labels
  const percentOfChart = (opts.labelWidthPercentage || 0.3) * (opts.fullPage ? 0.75 : 1);
  const labelsWidth = Math.ceil(settings.chartWidth * percentOfChart);
  settings.chartPadding.left = labelsWidth + 8; // added space for ellipses
  settings.labelsWidth = labelsWidth;
  return settings;
}

export const formatNumericAxisLabels = (value): string => {
  if (value < 10) return truncatedDecimals(value);
  return toSIFormat(value, 1);
};

export const determineTickCount = (rows: VictoryBarData): number => {
  if (isEmpty(rows)) {
    return 1;
  }

  const maxDataCount = rows.reduce(
    (accumulator, current) => (current.y > accumulator ? current.y : accumulator),
    0
  );

  return maxDataCount < 4 ? maxDataCount : 4;
};
